import { useRef } from "react";
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useLocation, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import useSWRMutation from "swr/mutation";
import useSWRInfinite from "swr/infinite";
import { useInView } from "react-intersection-observer";
import useSWR from "swr";

import HeaderGptSection from "./HeaderGptSection";
import CommentGptCard from "./CommentGptCard";
import CommentGptInput from "./CommentGptInput";
import EmptyGptComment from "./EmptyGptComment";
import UseWindowDimensions from "../../../utils/windowSize";
import {
  getSettingState,
  setCmState,
  setReplyComment,
} from "../../../redux/features/settingSlice";
import { ChunkUser } from "../../../utils/ChunkWhile";
import LoadingCommentGptCard from "./LoadingCommentGptCard";
import CustomHelmet from "./../CustomHelmet/index";
import Player from "../Player";
import { fetcherGetToken, fetcherPost } from "../../../services/fetcher";

const GptCommentPage = () => {
  const [comment, setComment] = useState("");
  const [conversRes, setConversRes] = useState(false);
  const theme = useTheme();
  const { userData, cmState } = useSelector(getSettingState);
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  let urlParams = location.pathname.split("/");
  const { height } = UseWindowDimensions();
  const ref = useRef(null);
  const tokenStorage = JSON.parse(localStorage.getItem("token")) || null;

  useEffect(() => {
    dispatch(setReplyComment(null));
  }, [dispatch]);

  const { ref: refTop, inView: vTop } = useInView({
    threshold: 0,
  });

  const { data: profile } = useSWR(() => {
    if (!userData && !urlParams[2]) {
      return null;
    }
    return [
      process.env.REACT_APP_API + `/auth/v2/profile/${params.profileId}/`,
      tokenStorage,
    ];
  }, fetcherGetToken);

  const { trigger } = useSWRMutation(
    () => {
      if (!profile) {
        return null;
      }
      return [`${process.env.REACT_APP_API}/qa/conversation/`, tokenStorage];
    },
    fetcherPost,
    {
      onSuccess: (e) => {
        setConversRes(e.pk);
      },
      onError: (e) => {
        if (
          e.response?.data.error[0].slice(0, 49) ===
          "There is a conversation for these users with this"
        ) {
          setConversRes(Number(e.response?.data.error[0].slice(53)));
        }
      },
    }
  );

  useEffect(() => {
    async function markAsRead() {
      await trigger({ responder_id: profile?.id });
    }
    profile && markAsRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const {
    data: comments,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData.results.length && !conversRes)
        return null;

      return [
        process.env.REACT_APP_API +
          `/qa/conversation/messages/${urlParams[2]}/?page=${
            pageIndex + 1
          }&page_size=30`,
        tokenStorage,
      ];
    },
    fetcherGetToken
    // {
    //   revalidateFirstPage: true,
    //   initialSize: 1,
    //   revalidateAll: false,
    // }
  );

  // Refresh Comment
  const useComments = () => {
    const { mutate } = useSWRInfinite(() => {
      if (!conversRes) return null;
      return [
        process.env.REACT_APP_API +
          `/qa/conversation/messages/${urlParams[2]}/?page=${1}&page_size=30`,
        tokenStorage,
      ];
    });
    return {
      refresh: mutate,
    };
  };

  const { refresh } = useComments();
  useEffect(() => {
    const interval = setInterval(refresh, 1600);

    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    vTop && moreInfinite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vTop]);

  const moreInfinite = () => {
    if (comments) {
      const moreTopicCart = size + 1 <= comments[0]?.total_pages;
      if (moreTopicCart) {
        setSize(size + 1);
        // dispatch(setLoadNavigation(true));
      } else {
        setSize(comments[0].total_pages);
      }
    }
  };

  useEffect(() => {
    if (comments) {
      var cmList = [];
      // eslint-disable-next-line array-callback-return
      comments.map((e) => {
        cmList.push(...e.results);
      });

      if (cmState && isValidating) {
        // console.log(1);
        const filterCm = cmList.filter(
          (obj1) => !cmState.some((obj2) => obj1.pk === obj2.pk)
        );

        dispatch(
          setCmState([...filterCm, ...cmState].sort((a, b) => b.pk - a.pk))
        );
      } else {
        // console.log(2);
        dispatch(setCmState([...cmList].sort((a, b) => b.pk - a.pk)));
      }
    }

    return () => {
      dispatch(setCmState(null));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, comments, location]);
  return (
    <>
      {<CustomHelmet title={`Ask Chat GPT about ${profile?.nickname}`} />}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          zIndex: 1,
          width: "100%",
        }}
      >
        <Box
          sx={{
            height: "50px",
            width: "100%",
            backgroundColor: "#00897B",
            position: "absolute",
          }}
        ></Box>
        <HeaderGptSection name={profile?.nickname} />
        <Box sx={{ display: "none" }}>
          <Player ref={ref} />
        </Box>

        <Box
          sx={{
            width: "100%",
            height: height - 146 + "px",
            overflowY: "scroll",
            mt: "81px",
            backgroundColor: theme.palette.white.main,
            display: "flex",
            flexDirection: "column",
            pb: "10px",
          }}
        >
          {!isLoading &&
            comments &&
            cmState &&
            ChunkUser(cmState)
              .reverse()
              .map((rowData, index) => {
                return (
                  <Box
                    key={index}
                    sx={{
                      my: "4px",
                      display: "flex",
                      flexDirection: "column-reverse",
                    }}
                  >
                    {rowData.map((row, i, { length }) => {
                      return (
                        <CommentGptCard
                          key={row.pk}
                          cmData={row}
                          firstIndex={i === 0}
                          lastIndex={i + 1 === length}
                        />
                      );
                    })}
                  </Box>
                );
              })}

          {comments && comments[0]?.results[0]?.author.id === userData && (
            <div className='animateContainer'>
              <div className='circleCell' />
              <div className='circleCell' />
              <div className='circleCell' />
            </div>
          )}

          {(isLoading || !comments) && (
            <Box sx={{ textAlign: "center", mt: "5px" }}>
              <LoadingCommentGptCard />
            </Box>
          )}
          {!isLoading && comments && (
            <Box
              ref={refTop}
              sx={{ display: "flex", justifyContent: "center", py: "2px" }}
            ></Box>
          )}
          {!isLoading && comments && cmState?.length === 0 && (
            <EmptyGptComment />
          )}
          <CommentGptInput
            conversRes={conversRes}
            profile={profile}
            comment={comment}
            setComment={setComment}
          />
        </Box>
      </Box>
    </>
  );
};

export default GptCommentPage;
